import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import type { WorkbookAgentPort } from './useWorkbookAgentTools';
import { applyAgentEdit } from './useWorkbookAgentWriteTools';
import {
  appOutputFor,
  appSlug,
  withLinkedApp,
  workbookAppGuide,
} from './workbookAppBuild';

/**
 * The on-page agent builds an App Output's App here, from this Workbook's
 * Worksheets. The server keeps one Report App per Output: the first build
 * creates it, later ones deploy a new version of it. An Output still linked to
 * some other App (an interactive one picked before) is relinked to its Report.
 */
export function useWorkbookAgentAppTools(
  workbookId: string,
  port: WorkbookAgentPort,
) {
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const queryClient = useQueryClient();
  const reopen = () =>
    queryClient.invalidateQueries({ queryKey: orpc.apps.openReport.key() });
  const catalogTool = useFrontendTool({
    name: 'app_view_catalog',
    description:
      "Read how an App Output's App is written before building one with build_app: its manifest, the feeds that read this workbook's worksheets (rows or an aggregation, filtered by view state) and the view components, actions and state bindings.",
    parameters: z.object({}),
    readOnlyHint: true,
    awaitable: true,
    execute: () => ({ guide: workbookAppGuide() }),
  });
  const buildTool = useFrontendTool({
    name: 'build_app',
    enabled: canUpdate,
    description:
      "Build the App shown in an App Output, from this workbook's worksheets: saves the workbook, then creates the Output's App and links it, or deploys a new version of the App it already has. Read app_view_catalog first. The server validates the manifest and refuses it with the reasons; fix them and try again. The App reads each worksheet's current result, so run the workbook first when a worksheet has none.",
    parameters: z.object({
      outputId: z.uuid().describe('The App Output to build the App for.'),
      name: z.string().min(1).max(160),
      description: z.string().max(2000).optional(),
      manifestJson: z
        .string()
        .max(1024 * 1024)
        .describe(
          'The manifest as JSON: {"schemaVersion": 1, "view": …, "feeds": {…}}.',
        ),
    }),
    awaitable: true,
    execute: async (input) => {
      const output = appOutputFor(port.snapshot().definition, input.outputId);
      const manifest = z
        .record(z.string(), z.unknown())
        .parse(JSON.parse(input.manifestJson));
      // The server checks the saved Workbook has this Output.
      await port.save();
      const app = await rpcClient.apps.createReport({
        workbookId,
        workbookOutputId: input.outputId,
        slug: appSlug(input.name, crypto.randomUUID().slice(0, 8)),
        name: input.name,
        description: input.description ?? null,
        manifest,
      });
      if (app.id !== output.appId)
        await applyAgentEdit(
          workbookId,
          port,
          null,
          (current) => withLinkedApp(current, input.outputId, app.id),
          [],
          () => input.outputId,
        );
      await reopen();
      return { appId: app.id };
    },
  });
  return [catalogTool, buildTool] as const;
}
